/**
 * Глобальная конфигурация Regle.
 *
 * Подключается в `main.ts` через `RegleVuePlugin` — после этого любой
 * вызов `useRegle()` в проекте:
 *   - получает локализованные сообщения для встроенных правил
 *     (`required`, `email`, `minLength`, `maxLength`);
 *   - имеет доступ к нашим кастомным правилам `phone` и `isTrue`.
 *
 * Сообщения берутся из i18n-неймспейса `validation.*`
 * (см. `src/i18n/locales/*.ts`, секция `validation`). Это единая точка
 * правды для текстов валидации на RU/EN.
 */
import { createRule, defineRegleOptions, type Maybe } from '@regle/core';
import { email, maxLength, minLength, required, withMessage } from '@regle/rules';
import { useI18n } from 'vue-i18n';

const PHONE_RE = /^[+\s()\-0-9]{7,25}$/;

/**
 * Телефон RU/интернациональный. Допускает пробелы, скобки, дефисы, `+`.
 * Пустая строка считается валидной (отдельным правилом `required` это
 * контролируется явно).
 */
export const phone = createRule({
  validator: (value: Maybe<string>) => {
    if (typeof value !== 'string') return true;
    const trimmed = value.trim();
    if (trimmed.length === 0) return true;
    if (!PHONE_RE.test(trimmed)) return false;
    const digits = trimmed.replace(/\D/g, '');
    return digits.length >= 7;
  },
  message: 'validation.phone',
});

/**
 * Строгое «должно быть `true`». Используется для обязательного чекбокса
 * согласия на обработку персональных данных.
 */
export const isTrue = createRule({
  validator: (value: Maybe<boolean>) => value === true,
  message: 'validation.consent',
});

/**
 * Глобальные опции Regle. Передаём в `app.use(RegleVuePlugin, regleOptions)`.
 *
 * Колбэк `rules: () => ({...})` вызывается внутри setup-контекста
 * каждого `useRegle`, поэтому `useI18n()` здесь легально: он возьмёт
 * ту же i18n-инстанцию, что и хостовой компонент.
 */
export const regleOptions = defineRegleOptions({
  rules: () => {
    const { t } = useI18n();
    return {
      required: withMessage(required, () => t('validation.required')),
      email: withMessage(email, () => t('validation.email')),
      minLength: withMessage(minLength, ({ $params: [min] }) => t('validation.minLen', { n: min })),
      maxLength: withMessage(maxLength, ({ $params: [max] }) => t('validation.maxLen', { n: max })),
      phone: withMessage(phone, () => t('validation.phone')),
      isTrue: withMessage(isTrue, () => t('validation.consent')),
    };
  },
});

declare module '@regle/core' {
  interface CustomRules {
    phone: typeof phone;
    isTrue: typeof isTrue;
  }
}
